import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../services/api.service';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import {
  DEFAULT_MMS_RICH_CARD,
  DEFAULT_RCS_RICH_CARD,
  DEFAULT_SMS_EMAIL_DATA,
  EMPTY_MMS_RICH_CARD,
  EMPTY_RCS_RICH_CARD,
  EMPTY_SMS_EMAIL_DATA,
  DeliveryChannel,
  DeliveryMessageType,
  DeliveryPreviewTabId,
  MMS_FALLBACK_BEHAVIORS,
  RichCardFormData,
  SMS_EMAIL_PLACEHOLDERS,
  SmsEmailModalData,
  applyRichCardFields,
  clearRichCardFieldsFromForm,
  clearEmailFieldsFromForm,
  clearSmsFieldsFromForm,
  isSameFormData,
  normalizeChannelFlags,
  pickRichCardFields,
} from '../sms-delivery.mapper';
import {
  appendMmsCouponImageToFormData,
  appendRcsCouponImageToFormData,
  extractCampaignLogoUploadUrl,
  MMS_COUPON_IMAGE_FIELD,
  RCS_COUPON_IMAGE_FIELD,
} from '../campaign-logo-upload.util';

export type { DeliveryChannel, DeliveryMessageType, DeliveryPreviewTabId, SmsEmailModalData };
export { DEFAULT_SMS_EMAIL_DATA };

const MMS_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
const MMS_ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf'];

@Component({
  selector: 'app-create-sms-email-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-sms-email-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-sms-email-modal.component.scss',
})
export class CreateSmsEmailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() initialData: SmsEmailModalData | null = null;
  /** Preview tab active in optional settings when the modal was opened. */
  @Input() initialPreviewTab: DeliveryPreviewTabId | null = null;
  /** When false, SMS/MMS/RCS channel and fields are hidden (campaign has no SMS platform). */
  @Input() smsPlatformEnabled = true;
  /** When false, email channel and fields are hidden (campaign has no email platform). */
  @Input() emailPlatformEnabled = true;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<SmsEmailModalData>();

  formData: SmsEmailModalData = { ...EMPTY_SMS_EMAIL_DATA };
  readonly placeholders = SMS_EMAIL_PLACEHOLDERS;
  readonly mmsPlaceholders = DEFAULT_MMS_RICH_CARD;
  readonly rcsPlaceholders = DEFAULT_RCS_RICH_CARD;
  private initialFormSnapshot: SmsEmailModalData | null = null;

  couponFile: File | null = null;
  couponBlobUrl = '';
  couponFileName = '';
  private mmsRichCard: RichCardFormData = { ...DEFAULT_MMS_RICH_CARD };
  private rcsRichCard: RichCardFormData = { ...DEFAULT_RCS_RICH_CARD };
  private smsMessageBody = '';
  private emailSubject = '';
  private emailPreheader = '';
  private emailBody = '';
  /** Committed API image URLs per rich type (preview after save / load). */
  private mmsCommittedCouponImageUrl = '';
  private rcsCommittedCouponImageUrl = '';
  isUploadingCoupon = false;
  couponValidationError = '';
  couponTouched = false;
  messageBodyValidationError = '';
  bubble2TextBodyValidationError = '';

  readonly fallbackBehaviors = MMS_FALLBACK_BEHAVIORS;
  readonly maxCouponFileSizeBytes = 5 * 1024 * 1024;
  readonly maxSmsMessageLength = 160;
  readonly smsMessageLengthWarning =
    'Maximum character limit reached. Please reduce the content length';

  readonly messageTypes: { id: DeliveryMessageType; label: string; sublabel: string }[] = [
    { id: 'sms', label: 'SMS', sublabel: 'Text · 160 chars' },
    { id: 'mms', label: 'MMS', sublabel: 'Text + Image' },
    // { id: 'rcs', label: 'RCS', sublabel: 'Rich card' }
  ];

  private apiService = inject(ApiService);
  private messageService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['isOpen'] && this.isOpen) ||
      changes['initialData'] ||
      changes['initialPreviewTab'] ||
      changes['smsPlatformEnabled'] ||
      changes['emailPlatformEnabled']
    ) {
      this.populateFormFields();
    }
  }

  populateFormFields(): void {
    if (this.initialData && this.hasInitialDeliveryData(this.initialData)) {
      const channelFlags = normalizeChannelFlags(this.initialData);
      this.formData = { ...EMPTY_SMS_EMAIL_DATA, ...this.initialData, ...channelFlags };
      this.mmsRichCard = { ...(this.initialData.mmsRichCard ?? EMPTY_MMS_RICH_CARD) };
      this.rcsRichCard = { ...(this.initialData.rcsRichCard ?? EMPTY_RCS_RICH_CARD) };
      this.mmsCommittedCouponImageUrl = this.mmsRichCard.couponImageUrl?.trim() || '';
      this.rcsCommittedCouponImageUrl = this.rcsRichCard.couponImageUrl?.trim() || '';
      this.smsMessageBody = this.resolveSmsMessageBody(this.initialData);
      this.persistEmailFieldsFromData(this.initialData);
      if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
        this.applyRichCardForType(this.formData.messageType);
      } else {
        this.applySmsOnlyFormState();
      }
    } else {
      this.formData = { ...EMPTY_SMS_EMAIL_DATA, ...normalizeChannelFlags(EMPTY_SMS_EMAIL_DATA) };
      this.mmsRichCard = { ...EMPTY_MMS_RICH_CARD };
      this.rcsRichCard = { ...EMPTY_RCS_RICH_CARD };
      this.smsMessageBody = '';
      this.emailSubject = '';
      this.emailPreheader = '';
      this.emailBody = '';
      this.mmsCommittedCouponImageUrl = '';
      this.rcsCommittedCouponImageUrl = '';
      this.clearCouponFile();
    }
    this.applyPlatformChannelAvailability();
    this.applyInitialPreviewTab();
    this.syncChannelFormState();
    this.formData.messageBody = this.truncateSmsMessage(this.formData.messageBody);
    this.formData.bubble2TextBody = this.truncateSmsMessage(this.formData.bubble2TextBody);
    this.couponTouched = false;
    this.couponValidationError = '';
    this.messageBodyValidationError = '';
    this.bubble2TextBodyValidationError = '';
    this.validateMessageFieldLengths();
    this.initialFormSnapshot = this.cloneFormSnapshot(this.buildFormSnapshot());
  }

  get showChannelToggles(): boolean {
    return this.smsPlatformEnabled && this.emailPlatformEnabled;
  }

  get isEditMode(): boolean {
    return Boolean(this.initialData && this.hasInitialDeliveryData(this.initialData));
  }

  /** Restricts channels to campaign-selected platforms (sms / email). */
  private applyPlatformChannelAvailability(): void {
    if (!this.smsPlatformEnabled) {
      this.formData.smsChannelEnabled = false;
    }
    if (!this.emailPlatformEnabled) {
      this.formData.emailChannelEnabled = false;
    }

    if (this.smsPlatformEnabled && !this.emailPlatformEnabled) {
      this.formData.smsChannelEnabled = true;
      this.formData.emailChannelEnabled = false;
    } else if (!this.smsPlatformEnabled && this.emailPlatformEnabled) {
      this.formData.smsChannelEnabled = false;
      this.formData.emailChannelEnabled = true;
    } else if (this.smsPlatformEnabled && this.emailPlatformEnabled) {
      if (!this.formData.smsChannelEnabled && !this.formData.emailChannelEnabled) {
        this.formData.smsChannelEnabled = true;
      }
    }

    const flags = normalizeChannelFlags(this.formData);
    this.formData.smsChannelEnabled = flags.smsChannelEnabled;
    this.formData.emailChannelEnabled = flags.emailChannelEnabled;
    this.formData.channel = flags.channel;
  }

  /** Aligns channel toggles and message type with the tab the user had selected in optional settings. */
  private applyInitialPreviewTab(): void {
    const tab = this.initialPreviewTab;
    if (!tab) {
      return;
    }

    if (tab === 'email') {
      if (!this.emailPlatformEnabled) {
        return;
      }
      this.formData.emailChannelEnabled = true;
    } else {
      if (!this.smsPlatformEnabled) {
        return;
      }
      this.formData.smsChannelEnabled = true;
      if (tab === 'sms') {
        if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
          this.persistActiveRichCard();
        }
        this.formData.messageType = 'sms';
      } else {
        this.selectMessageType(tab);
      }
    }

    const flags = normalizeChannelFlags(this.formData);
    this.formData.smsChannelEnabled = flags.smsChannelEnabled;
    this.formData.emailChannelEnabled = flags.emailChannelEnabled;
    this.formData.channel = flags.channel;
  }

  /** Ensures Save/Reset react to ngModel edits when SMS + Email are both enabled. */
  onFormFieldChange(): void {
    this.cdr.markForCheck();
  }

  onMessageBodyChange(): void {
    this.formData.messageBody = this.truncateSmsMessage(this.formData.messageBody);
    if (this.formData.messageType === 'sms') {
      this.smsMessageBody = this.formData.messageBody || '';
    }
    this.validateMessageBodyLength();
    this.onFormFieldChange();
  }

  onBubble2TextBodyChange(): void {
    this.formData.bubble2TextBody = this.truncateSmsMessage(this.formData.bubble2TextBody);
    this.validateBubble2TextBodyLength();
    this.onFormFieldChange();
  }

  onEmailFieldChange(): void {
    this.persistEmailFields();
    this.onFormFieldChange();
  }

  insertTag(tag: string, targetField: 'messageBody' | 'bubble2TextBody' | 'emailBody'): void {
    const tagWithBrackets = `{{${tag}}}`;
    const textareaId =
      targetField === 'messageBody'
        ? 'delivery-message-body'
        : targetField === 'bubble2TextBody'
          ? 'rich-bubble-2-body'
          : 'email-body';
    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement | null;
    const currentValue = this.getMergeTagFieldValue(targetField);

    let newValue: string;
    let cursorPosition: number;

    if (textarea) {
      const start = textarea.selectionStart ?? currentValue.length;
      const end = textarea.selectionEnd ?? start;
      newValue = currentValue.slice(0, start) + tagWithBrackets + currentValue.slice(end);
      cursorPosition = start + tagWithBrackets.length;
    } else {
      newValue = currentValue + tagWithBrackets;
      cursorPosition = newValue.length;
    }

    this.setMergeTagFieldValue(targetField, newValue);

    if (textarea) {
      setTimeout(() => {
        textarea.focus();
        const pos = Math.min(cursorPosition, textarea.value.length);
        textarea.setSelectionRange(pos, pos);
      });
    }
  }

  private getMergeTagFieldValue(
    targetField: 'messageBody' | 'bubble2TextBody' | 'emailBody'
  ): string {
    switch (targetField) {
      case 'messageBody':
        return this.formData.messageBody || '';
      case 'bubble2TextBody':
        return this.formData.bubble2TextBody || '';
      case 'emailBody':
        return this.formData.emailBody || '';
    }
  }

  private setMergeTagFieldValue(
    targetField: 'messageBody' | 'bubble2TextBody' | 'emailBody',
    value: string
  ): void {
    switch (targetField) {
      case 'messageBody':
        this.formData.messageBody = value;
        this.onMessageBodyChange();
        break;
      case 'bubble2TextBody':
        this.formData.bubble2TextBody = value;
        this.onBubble2TextBodyChange();
        break;
      case 'emailBody':
        this.formData.emailBody = value;
        this.onEmailFieldChange();
        break;
    }
  }

  get hasFormChanges(): boolean {
    if (!this.initialFormSnapshot) {
      return false;
    }
    return !isSameFormData(this.buildFormSnapshot(), this.initialFormSnapshot);
  }

  get canSave(): boolean {
    return this.hasFormChanges && this.isFormValid();
  }

  get canReset(): boolean {
    return this.hasFormChanges;
  }

  get selectedChannelLabel(): string {
    const parts: string[] = [];
    if (this.formData.smsChannelEnabled && this.smsPlatformEnabled) {
      parts.push('SMS');
    }
    if (this.formData.emailChannelEnabled && this.emailPlatformEnabled) {
      parts.push('Email');
    }
    if (parts.length) {
      return parts.join(' + ');
    }
    if (this.emailPlatformEnabled) {
      return 'Email';
    }
    return 'SMS';
  }

  get isMmsForm(): boolean {
    return this.formData.smsChannelEnabled && this.formData.messageType === 'mms';
  }

  get isRcsForm(): boolean {
    return this.formData.smsChannelEnabled && this.formData.messageType === 'rcs';
  }

  get isRichCouponForm(): boolean {
    return this.isMmsForm || this.isRcsForm;
  }

  get isSplitEnabled(): boolean {
    return !!this.formData?.splitTwoBubbles;
  }

  onSplitToggleChange(value: boolean): void {
    this.formData.splitTwoBubbles = value;
    this.cdr.detectChanges();
  }

  get richMessageTypeLabel(): 'MMS' | 'RCS' {
    return this.isRcsForm ? 'RCS' : 'MMS';
  }

  get couponPreviewUrl(): string {
    if (this.couponBlobUrl) {
      return this.couponBlobUrl;
    }
    return this.isRcsForm ? this.rcsCommittedCouponImageUrl : this.mmsCommittedCouponImageUrl;
  }

  onClose(): void {
    this.revokeCouponBlobUrl();
    this.close.emit();
  }

  onReset(): void {
    if (!this.initialFormSnapshot || !this.canReset) {
      return;
    }
    this.formData = { ...this.cloneFormSnapshot(this.initialFormSnapshot) };
    this.mmsRichCard = { ...(this.formData.mmsRichCard ?? EMPTY_MMS_RICH_CARD) };
    this.rcsRichCard = { ...(this.formData.rcsRichCard ?? EMPTY_RCS_RICH_CARD) };
    this.smsMessageBody = this.resolveSmsMessageBody(this.formData);
    this.persistEmailFieldsFromData(this.formData);
    this.mmsCommittedCouponImageUrl = this.mmsRichCard.couponImageUrl?.trim() || '';
    this.rcsCommittedCouponImageUrl = this.rcsRichCard.couponImageUrl?.trim() || '';
    this.syncChannelFormState();
    this.couponTouched = false;
    this.couponValidationError = '';
    this.messageBodyValidationError = '';
    this.bubble2TextBodyValidationError = '';
    this.onFormFieldChange();
  }

  onSave(): void {
    this.applyPlaceholderDefaults();
    this.validateMessageFieldLengths();
    if (!this.isFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Complete delivery details!',
        detail: this.getSaveValidationDetail(),
      });
      return;
    }
    if (!this.canSave) {
      return;
    }
    if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
      this.persistActiveRichCard();
    } else if (this.formData.messageType === 'sms') {
      this.smsMessageBody = this.formData.messageBody || '';
    }
    if (this.formData.emailChannelEnabled) {
      this.persistEmailFields();
    }
    this.mmsCommittedCouponImageUrl = this.mmsRichCard.couponImageUrl?.trim() || '';
    this.rcsCommittedCouponImageUrl = this.rcsRichCard.couponImageUrl?.trim() || '';
    this.revokeCouponBlobUrl();
    this.couponFile = null;
    this.save.emit({
      ...this.buildFormSnapshot(),
      mmsRichCard: { ...this.mmsRichCard },
      rcsRichCard: { ...this.rcsRichCard },
    });
  }

  isFormValid(): boolean {
    const smsValid = !this.formData.smsChannelEnabled || this.isSmsChannelValid();
    const emailValid = !this.formData.emailChannelEnabled || this.isEmailChannelValid();
    return smsValid && emailValid;
  }

  toggleChannel(channel: DeliveryChannel): void {
    if (channel === 'sms' && !this.smsPlatformEnabled) {
      return;
    }
    if (channel === 'email' && !this.emailPlatformEnabled) {
      return;
    }

    if (channel === 'sms') {
      if (this.formData.smsChannelEnabled) {
        if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
          this.persistActiveRichCard();
        } else if (this.formData.messageType === 'sms') {
          this.smsMessageBody = this.formData.messageBody || '';
        }
      }
      this.formData.smsChannelEnabled = !this.formData.smsChannelEnabled;
    } else {
      if (this.formData.emailChannelEnabled) {
        this.persistEmailFields();
      }
      this.formData.emailChannelEnabled = !this.formData.emailChannelEnabled;
    }

    if (!this.formData.smsChannelEnabled && !this.formData.emailChannelEnabled) {
      if (channel === 'sms' && this.emailPlatformEnabled) {
        this.formData.emailChannelEnabled = true;
      } else if (this.smsPlatformEnabled) {
        this.formData.smsChannelEnabled = true;
      }
    }
    const flags = normalizeChannelFlags(this.formData);
    this.formData.smsChannelEnabled = flags.smsChannelEnabled;
    this.formData.emailChannelEnabled = flags.emailChannelEnabled;
    this.formData.channel = flags.channel;
    this.syncChannelFormState();
    this.onFormFieldChange();
  }

  selectMessageType(type: DeliveryMessageType): void {
    if (!this.formData.smsChannelEnabled) {
      return;
    }
    if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
      this.persistActiveRichCard();
    } else if (this.formData.messageType === 'sms') {
      this.smsMessageBody = this.formData.messageBody || '';
    }

    this.formData.messageType = type;
    if (type === 'mms' || type === 'rcs') {
      this.applyRichCardForType(type);
    } else {
      this.applySmsOnlyFormState();
    }
    this.couponTouched = false;
    this.couponValidationError = '';
    this.messageBodyValidationError = '';
    this.bubble2TextBodyValidationError = '';
    this.onFormFieldChange();
  }

  getPreviewMessage(): string {
    const resolved = this.getResolvedFormData();
    const source =
      this.isRichCouponForm && this.formData.splitTwoBubbles
        ? resolved.bubble2TextBody
        : resolved.messageBody;
    const richDefaults = this.isRcsForm ? DEFAULT_RCS_RICH_CARD : DEFAULT_MMS_RICH_CARD;
    const text =
      source?.trim() ||
      (this.isRichCouponForm && this.formData.splitTwoBubbles
        ? richDefaults.bubble2TextBody
        : SMS_EMAIL_PLACEHOLDERS.messageBody);
    return this.applyPreviewSubstitutions(text);
  }

  getPreviewCaption(): string {
    return this.formData.imageCaption?.trim() || '';
  }

  getPreviewAltText(): string {
    const richDefaults = this.isRcsForm ? DEFAULT_RCS_RICH_CARD : DEFAULT_MMS_RICH_CARD;
    return this.formData.imageAltText?.trim() || richDefaults.imageAltText;
  }

  getEmailPreviewSubject(): string {
    return this.applyPreviewSubstitutions(
      this.emailSubject?.trim() || SMS_EMAIL_PLACEHOLDERS.emailSubject
    );
  }

  getEmailPreviewPreheader(): string {
    return this.applyPreviewSubstitutions(
      this.emailPreheader?.trim() || SMS_EMAIL_PLACEHOLDERS.emailPreheader
    );
  }

  getEmailPreviewBody(): string {
    const raw = this.emailBody?.trim() || SMS_EMAIL_PLACEHOLDERS.emailBody;
    return this.applyPreviewSubstitutions(this.stripHtmlForPreview(raw));
  }

  /** Saves exactly what the user typed; blank fields stay blank instead of being backfilled with placeholder copy. */
  private applyPlaceholderDefaults(): void {
    if (this.formData.smsChannelEnabled) {
      if (this.formData.messageType === 'sms') {
        this.smsMessageBody = this.formData.messageBody?.trim() || '';
        this.formData.messageBody = this.smsMessageBody;
      }

      if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
        this.formData.bubble2TextBody = this.formData.bubble2TextBody?.trim() || '';
        this.formData.fallbackText = this.formData.fallbackText?.trim() || '';
        this.formData.imageAltText = this.formData.imageAltText?.trim() || '';
        this.formData.imageCaption = this.formData.imageCaption?.trim() || '';
        this.persistActiveRichCard();
      }
    }

    if (this.formData.emailChannelEnabled) {
      this.applyEmailFieldsToForm();
    }
  }

  private getResolvedFormData(): SmsEmailModalData {
    const p = SMS_EMAIL_PLACEHOLDERS;
    const richP =
      this.formData.messageType === 'rcs' ? DEFAULT_RCS_RICH_CARD : DEFAULT_MMS_RICH_CARD;
    return {
      ...this.formData,
      messageBody: this.smsMessageBody?.trim() || p.messageBody,
      emailSubject: this.emailSubject?.trim() || '',
      emailPreheader: this.emailPreheader?.trim() || '',
      emailBody: this.emailBody?.trim() || '',
      bubble2TextBody: this.formData.bubble2TextBody?.trim() || richP.bubble2TextBody,
      fallbackText: this.formData.fallbackText?.trim() || richP.fallbackText,
    };
  }

  private isSmsChannelValid(): boolean {
    const resolved = this.getResolvedFormData();
    if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
      return Boolean(
        this.formData.couponImageUrl?.trim() &&
        this.formData.fallbackText?.trim() &&
        !this.isUploadingCoupon &&
        this.isBubble2TextBodyLengthValid()
      );
    }
    if (this.formData.messageType === 'sms') {
      return Boolean(this.smsMessageBody?.trim() && this.isMessageBodyLengthValid());
    }
    return Boolean(resolved.messageBody?.trim() && this.isMessageBodyLengthValid());
  }

  private isEmailChannelValid(): boolean {
    return Boolean(this.emailSubject?.trim());
  }

  private stripHtmlForPreview(html: string): string {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  async onCouponUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    this.couponValidationError = '';
    this.couponTouched = true;
    const validationError = this.getCouponFileValidationError(file);
    if (validationError) {
      this.couponValidationError = validationError;
      this.cdr.detectChanges();
      return;
    }

    this.couponFile = file;
    this.couponFileName = file.name;
    this.revokeCouponBlobUrl();
    this.couponBlobUrl = URL.createObjectURL(file);
    this.isUploadingCoupon = true;
    this.cdr.detectChanges();
    this.uploadCoupon(file);
  }

  private uploadCoupon(file: File): void {
    const formData = new FormData();
    const isRcs = this.formData.messageType === 'rcs';
    if (isRcs) {
      appendRcsCouponImageToFormData(formData, file);
    } else {
      appendMmsCouponImageToFormData(formData, file);
    }

    this.apiService.uploadCampaignFile(formData).subscribe({
      next: async response => {
        const url =
          extractCampaignLogoUploadUrl(
            response,
            isRcs ? RCS_COUPON_IMAGE_FIELD : MMS_COUPON_IMAGE_FIELD
          ) ||
          extractCampaignLogoUploadUrl(response, isRcs ? 'rcs_coupon_image' : 'mms_coupon_image') ||
          extractCampaignLogoUploadUrl(response, 'mms_coupon_image');
        if (url) {
          this.formData.couponImageUrl = url;
          this.persistActiveRichCard();
          this.couponValidationError = '';
          await this.setCouponDimensionsFromFile(file);
        } else {
          this.couponValidationError = 'Image upload failed. Please try again.';
          this.clearCouponAfterFailedUpload();
        }
        this.isUploadingCoupon = false;
        this.onFormFieldChange();
        this.cdr.detectChanges();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Upload failed',
          detail: 'Could not upload coupon image. Please try again.',
        });
        this.clearCouponAfterFailedUpload();
        this.isUploadingCoupon = false;
        this.cdr.detectChanges();
      },
    });
  }

  removeCoupon(): void {
    this.clearCouponFile();
    this.formData.couponImageUrl = '';
    if (this.isRcsForm) {
      this.rcsCommittedCouponImageUrl = '';
      this.rcsRichCard = {
        ...this.rcsRichCard,
        couponImageUrl: '',
        imageWidth: DEFAULT_RCS_RICH_CARD.imageWidth,
        imageHeight: DEFAULT_RCS_RICH_CARD.imageHeight,
      };
    } else {
      this.mmsCommittedCouponImageUrl = '';
      this.mmsRichCard = {
        ...this.mmsRichCard,
        couponImageUrl: '',
        imageWidth: DEFAULT_MMS_RICH_CARD.imageWidth,
        imageHeight: DEFAULT_MMS_RICH_CARD.imageHeight,
      };
    }
    this.couponTouched = true;
  }

  private async setCouponDimensionsFromFile(file: File): Promise<void> {
    if (!file.type.startsWith('image/')) {
      return;
    }
    const dimensions = await this.readImageDimensions(file);
    if (dimensions) {
      this.formData.imageWidth = dimensions.width;
      this.formData.imageHeight = dimensions.height;
    }
  }

  private readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  private getCouponFileValidationError(file: File): string {
    const lowerName = file.name.toLowerCase();
    const hasAllowedExtension = MMS_ALLOWED_EXTENSIONS.some(ext => lowerName.endsWith(ext));
    const hasAllowedType = MMS_ALLOWED_TYPES.includes(file.type);
    if (!hasAllowedType && !hasAllowedExtension) {
      return 'Only PDF, PNG, JPG, and JPEG files are allowed';
    }
    if (file.size > this.maxCouponFileSizeBytes) {
      return 'File size must be 5MB or less';
    }
    return '';
  }

  private validateMessageFieldLengths(): void {
    this.validateMessageBodyLength();
    this.validateBubble2TextBodyLength();
  }

  private validateMessageBodyLength(): void {
    this.messageBodyValidationError = this.getSmsMessageLengthWarning(
      (this.formData.messageBody || '').length
    );
  }

  private validateBubble2TextBodyLength(): void {
    this.bubble2TextBodyValidationError = this.getSmsMessageLengthWarning(
      (this.formData.bubble2TextBody || '').length
    );
  }

  private getSmsMessageLengthWarning(length: number): string {
    return length >= this.maxSmsMessageLength ? this.smsMessageLengthWarning : '';
  }

  private truncateSmsMessage(value: string | null | undefined): string {
    const text = value || '';
    return text.length > this.maxSmsMessageLength ? text.slice(0, this.maxSmsMessageLength) : text;
  }

  private isMessageBodyLengthValid(): boolean {
    return (this.formData.messageBody || '').length <= this.maxSmsMessageLength;
  }

  private isBubble2TextBodyLengthValid(): boolean {
    return (this.formData.bubble2TextBody || '').length <= this.maxSmsMessageLength;
  }

  private getSaveValidationDetail(): string {
    if (this.messageBodyValidationError || this.bubble2TextBodyValidationError) {
      return this.smsMessageLengthWarning;
    }
    return 'Fill in the empty fields to save.';
  }

  private hasInitialDeliveryData(data: SmsEmailModalData): boolean {
    const flags = normalizeChannelFlags(data);
    if (flags.emailChannelEnabled) {
      return Boolean(
        data.emailSubject?.trim() || data.emailPreheader?.trim() || data.emailBody?.trim()
      );
    }
    if (data.messageType === 'mms' || data.messageType === 'rcs') {
      const rich =
        data.messageType === 'rcs'
          ? (data.rcsRichCard ?? pickRichCardFields(data))
          : (data.mmsRichCard ?? pickRichCardFields(data));
      return Boolean(
        rich.bubble2TextBody?.trim() || rich.couponImageUrl?.trim() || rich.fallbackText?.trim()
      );
    }
    return Boolean(data.messageBody?.trim() || data.senderContact?.trim());
  }

  private persistActiveRichCard(): void {
    const rich = pickRichCardFields(this.formData);
    if (this.formData.messageType === 'rcs') {
      this.rcsRichCard = { ...rich };
      return;
    }
    if (this.formData.messageType === 'mms') {
      this.mmsRichCard = { ...rich };
    }
  }

  private applyRichCardForType(type: 'mms' | 'rcs'): void {
    const rich = type === 'rcs' ? { ...this.rcsRichCard } : { ...this.mmsRichCard };
    this.formData = applyRichCardFields(this.formData, rich);
    this.revokeCouponBlobUrl();
    this.couponFile = null;
    const committed =
      type === 'rcs' ? this.rcsCommittedCouponImageUrl : this.mmsCommittedCouponImageUrl;
    if (committed) {
      this.couponFileName = this.getDisplayNameForUrl(committed);
    } else {
      this.couponFileName = '';
    }
  }

  private getCommittedCouponImageUrl(): string {
    return this.isRcsForm ? this.rcsCommittedCouponImageUrl : this.mmsCommittedCouponImageUrl;
  }

  private clearCouponFile(): void {
    this.couponFile = null;
    this.couponFileName = '';
    this.revokeCouponBlobUrl();
  }

  /** Clears local preview and reverts pending upload URL after a failed upload. */
  private clearCouponAfterFailedUpload(): void {
    this.revokeCouponBlobUrl();
    this.couponFile = null;
    const committed = this.getCommittedCouponImageUrl();
    this.formData.couponImageUrl = committed;
    this.couponFileName = committed ? this.getDisplayNameForUrl(committed) : '';
  }

  private revokeCouponBlobUrl(): void {
    if (this.couponBlobUrl) {
      URL.revokeObjectURL(this.couponBlobUrl);
      this.couponBlobUrl = '';
    }
  }

  private getDisplayNameForUrl(url: string): string {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const segment = pathname.split('/').filter(Boolean).pop();
      return segment || 'coupon-image';
    } catch {
      return 'coupon-image';
    }
  }

  private applyPreviewSubstitutions(text: string): string {
    return text.replace(/\{\{?\s*first_?name\s*\}?\}/gi, 'Alex');
  }

  private buildFormSnapshot(): SmsEmailModalData {
    if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
      this.persistActiveRichCard();
    } else if (this.formData.messageType === 'sms') {
      this.smsMessageBody = this.formData.messageBody || '';
    }
    if (this.formData.emailChannelEnabled) {
      this.persistEmailFields();
    }

    const channelFlags = normalizeChannelFlags(this.formData);
    let snapshot: SmsEmailModalData = {
      ...this.formData,
      ...channelFlags,
      emailSubject: this.emailSubject,
      emailPreheader: this.emailPreheader,
      emailBody: this.emailBody,
      mmsRichCard: { ...this.mmsRichCard },
      rcsRichCard: { ...this.rcsRichCard },
    };

    if (this.formData.messageType === 'mms') {
      snapshot = applyRichCardFields(snapshot, this.mmsRichCard);
    } else if (this.formData.messageType === 'rcs') {
      snapshot = applyRichCardFields(snapshot, this.rcsRichCard);
    } else if (this.formData.smsChannelEnabled) {
      snapshot = {
        ...clearRichCardFieldsFromForm(snapshot),
        messageType: 'sms',
        messageBody: this.smsMessageBody,
      };
    }

    return {
      ...snapshot,
      emailSubject: this.emailSubject?.trim() || '',
      emailPreheader: this.emailPreheader?.trim() || '',
      emailBody: this.emailBody?.trim() || '',
      mmsRichCard: { ...this.mmsRichCard },
      rcsRichCard: { ...this.rcsRichCard },
    };
  }

  private cloneFormSnapshot(data: SmsEmailModalData): SmsEmailModalData {
    const channelFlags = normalizeChannelFlags(data);
    let snapshot: SmsEmailModalData = {
      ...data,
      ...channelFlags,
      mmsRichCard: { ...(data.mmsRichCard ?? EMPTY_MMS_RICH_CARD) },
      rcsRichCard: { ...(data.rcsRichCard ?? EMPTY_RCS_RICH_CARD) },
    };
    if (data.messageType === 'mms') {
      snapshot = applyRichCardFields(snapshot, snapshot.mmsRichCard ?? EMPTY_MMS_RICH_CARD);
    } else if (data.messageType === 'rcs') {
      snapshot = applyRichCardFields(snapshot, snapshot.rcsRichCard ?? EMPTY_RCS_RICH_CARD);
    } else {
      snapshot = {
        ...clearRichCardFieldsFromForm(snapshot),
        messageType: 'sms',
        messageBody: data.messageBody || '',
      };
    }
    return snapshot;
  }

  private syncChannelFormState(): void {
    if (this.formData.smsChannelEnabled) {
      if (this.formData.messageType === 'mms' || this.formData.messageType === 'rcs') {
        this.applyRichCardForType(this.formData.messageType);
      } else {
        this.applySmsOnlyFormState();
      }
    } else {
      this.clearCouponFile();
      this.formData = clearSmsFieldsFromForm(this.formData);
    }

    if (this.formData.emailChannelEnabled) {
      this.applyEmailFieldsToForm();
    } else {
      this.formData = clearEmailFieldsFromForm(this.formData);
    }
  }

  private persistEmailFields(): void {
    this.emailSubject = this.formData.emailSubject?.trim() || '';
    this.emailPreheader = this.formData.emailPreheader?.trim() || '';
    this.emailBody = this.formData.emailBody?.trim() || '';
  }

  private persistEmailFieldsFromData(data: SmsEmailModalData): void {
    this.emailSubject = data.emailSubject?.trim() || '';
    this.emailPreheader = data.emailPreheader?.trim() || '';
    this.emailBody = data.emailBody?.trim() || '';
  }

  private applyEmailFieldsToForm(): void {
    this.formData.emailSubject = this.emailSubject;
    this.formData.emailPreheader = this.emailPreheader;
    this.formData.emailBody = this.emailBody;
  }

  private applySmsOnlyFormState(): void {
    this.clearCouponFile();
    this.formData = clearRichCardFieldsFromForm(this.formData);
    this.formData.messageType = 'sms';
    this.formData.messageBody = this.smsMessageBody;
  }

  private resolveSmsMessageBody(data: SmsEmailModalData): string {
    if (data.messageType === 'sms') {
      return data.messageBody?.trim() || '';
    }
    return data.messageBody?.trim() || '';
  }
}
